import React from 'react';
import { Account, ROLE_TARGET_ID } from '@packages/models/api';
import { AccountLocation as AccountLocationTable, Typography } from '@packages/ui/shared';
import { AccountLocationExtended } from '../manage-users-panel';
import Select from '@web/components/select';

interface AccountLocationsProps {
    account?: Account;
    disabled?: boolean;
    handleDistrictSelectChange: (locationId: string, districtId: string) => void;
    handleRegionSelectChange: (locationId: string, regionId: string) => void;
    handleZoneSelectChange: (locationId: string, zoneId: string) => void;
    locations: AccountLocationExtended[];
}

const renderSelect = (
    label: string,
    value: string,
    options: { value: string; title: string }[],
    onChange: (event: React.ChangeEvent<HTMLSelectElement>) => void,
    disabled: boolean
) => (
    <div className="mb-2">
        <div className="mb-2">
            <Typography variant="default">{label}</Typography>
        </div>
        <Select value={value} options={options} onChange={onChange} disabled={disabled} />
    </div>
);

const LocationHeader: React.FC<{ title: string }> = ({ title }) => (
    <div className="mb-1">
        <Typography variant="caption2" color="graySix">
            {title}
        </Typography>
    </div>
);

export const AccountLocations: React.FC<AccountLocationsProps> = ({
    account,
    disabled = false,
    locations,
    handleRegionSelectChange,
    handleDistrictSelectChange,
    handleZoneSelectChange,
}) => {
    return (
        <>
            <div className="mb-2">
                <LocationHeader title="Default Location" />
                <div className="mb-2">
                    <Typography>{account?.locationsDescription ?? 'N/A'}</Typography>
                </div>
                <hr />
            </div>

            <div className="mb-0">
                {locations.map((location, index) => {
                    const isRegionOrDistrict =
                        location.roleTargetTypeId === ROLE_TARGET_ID.REGION ||
                        location.roleTargetTypeId === ROLE_TARGET_ID.DISTRICT;

                    const jobTitle = location.elements.find((e) => e.accountLocationElementType === 'JOB_TITLE')?.value;

                    return (
                        <React.Fragment key={location.locationId}>
                            {!isRegionOrDistrict && (
                                <>
                                    <LocationHeader title="Location" />
                                    <AccountLocationTable elements={location.elements} index={index} />
                                </>
                            )}

                            {isRegionOrDistrict && (
                                <>
                                    <LocationHeader title="Region, Zone, District" />
                                    <div className="mb-1">
                                        <Typography>{jobTitle}</Typography>
                                    </div>

                                    {renderSelect(
                                        'Region',
                                        location.regionSelection,
                                        location.regionOptions.map((region) => ({
                                            value: region.regionId,
                                            title: region.description,
                                        })),
                                        (event) =>
                                            handleRegionSelectChange(location.locationId, event.currentTarget.value),
                                        disabled || location.isLoading
                                    )}

                                    {location.roleTargetTypeId === ROLE_TARGET_ID.DISTRICT && (
                                        <>
                                            {renderSelect(
                                                'Zone',
                                                location.zoneSelection,
                                                location.zoneOptions.map((zone) => ({
                                                    value: zone.zoneId,
                                                    title: zone.description,
                                                })),
                                                (event) =>
                                                    handleZoneSelectChange(
                                                        location.locationId,
                                                        event.currentTarget.value
                                                    ),
                                                disabled || location.isLoading
                                            )}

                                            {renderSelect(
                                                'District',
                                                location.districtSelection,
                                                location.districtOptions.map((district) => ({
                                                    value: district.districtId,
                                                    title: district.description,
                                                })),
                                                (event) =>
                                                    handleDistrictSelectChange(
                                                        location.locationId,
                                                        event.currentTarget.value
                                                    ),
                                                disabled || location.isLoading
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        </>
    );
};
